import { AlertTriangle, X } from 'lucide-react';
import { useState } from 'react';
import { useParks } from '../../contexts/ParksContext';

// AlarmBanner — kuvab aktiivsete alarmide riba ülaservas.
// Kogub alarmid KÕIKIDEST parkidest:
//   - BAU fault/alarm bitid (active_alarms, severity = critical/warning/info)
//   - Vedelikjahutuse vead (lc_active_faults — kõik kriitilised)
//   - Suitsu/temp alarm (tempAndSmokeAlarm)
//   - DI sisendid kui need viitavad alarmile
// Kuvab vaid CRITICAL ja WARNING. Info-tasemelised peidetud (info/standby olek).

export function AlarmBanner({ onDismiss }) {
  const [dismissed, setDismissed] = useState(false);
  const { data } = useParks();

  if (dismissed) return null;
  if (!data?.parks) return null;

  // Kogume kõik aktiivsed alarmid kõikidest parkidest
  const events = [];
  for (const p of data.parks) {
    if (!p.online || !p.bau) continue;
    const parkLabel = p.name || `Slot ${p.slot}`;

    // BAU fault/alarm bittid (juba dekodeeritud nimedeks backend'is)
    if (Array.isArray(p.bau.active_alarms)) {
      for (const a of p.bau.active_alarms) {
        if (a.severity === 'critical' || a.severity === 'warning') {
          events.push({
            park: parkLabel, severity: a.severity,
            text: a.name,
            tag: a.source === 'fault' ? 'BAU fault' : `BAU L${a.level}`,
          });
        }
      }
    }

    // Vedelikjahutuse vead
    if (Array.isArray(p.bau.lc_active_faults)) {
      for (const f of p.bau.lc_active_faults) {
        events.push({
          park: parkLabel, severity: 'warning',
          text: f.name, tag: 'Vedelikjahutus',
        });
      }
    }

    // Suitsu/temp alarm — alati kriitiline
    if (p.bau.tempAndSmokeAlarm) {
      events.push({
        park: parkLabel, severity: 'critical',
        text: 'Suitsu/temp alarm', tag: 'Ohutus',
      });
    }

    // PCS sidetõrge — skipi Markedroidi-üleandmise puhul (kavatsuslik, mitte viga)
    if (p.pcs_host && !p.pcs_online && !p.control?.pcs_polling_disabled) {
      events.push({
        park: parkLabel, severity: 'warning',
        text: 'PCS ei vasta', tag: 'PCS',
      });
    }
  }

  // Filtreeri info'd välja, säilitame ainult critical + warning
  const critical = events.filter((e) => e.severity === 'critical');
  const warnings = events.filter((e) => e.severity === 'warning');

  if (critical.length === 0 && warnings.length === 0) return null;

  // Värvid sõltuvad raskusastmest
  const isCritical = critical.length > 0;
  const bg = isCritical ? 'bg-red-50' : 'bg-amber-50';
  const border = isCritical ? 'border-red-200' : 'border-amber-200';
  const iconC = isCritical ? 'text-red-600' : 'text-amber-600';
  const titleC = isCritical ? 'text-red-700' : 'text-amber-700';
  const textC = isCritical ? 'text-red-600' : 'text-amber-700';

  // Kuvame kõige tähtsamad esimesed (kriitilised), kuni 6 tükki
  const top = [...critical, ...warnings].slice(0, 6);
  const total = critical.length + warnings.length;

  return (
    <div className={`${bg} border-b ${border} px-4 py-2`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 flex-wrap">
          <AlertTriangle className={`w-4 h-4 ${iconC} animate-pulse flex-shrink-0`} />
          <span className={`text-[12px] font-semibold ${titleC}`}>
            {critical.length > 0 && (
              <>{critical.length} kriitilist{warnings.length > 0 ? ' · ' : ''}</>
            )}
            {warnings.length > 0 && <>{warnings.length} hoiatust</>}
          </span>
          <span className={`text-[11px] ${textC} flex flex-wrap items-center gap-1.5`}>
            {top.map((e, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/60 border border-current/10" style={{ borderColor: 'currentColor' + '30' }}>
                <span style={{ opacity: 0.6, fontFamily: 'JetBrains Mono, monospace', fontSize: '0.62rem' }}>
                  {e.park.replace(/^Alaküla /, 'A')} · {e.tag}
                </span>
                <span style={{ fontWeight: 500 }}>{e.text}</span>
              </span>
            ))}
            {total > top.length && (
              <span style={{ opacity: 0.6 }}>+{total - top.length} muud</span>
            )}
          </span>
        </div>

        <button
          onClick={() => {
            setDismissed(true);
            onDismiss?.();
          }}
          className={`p-1 ${iconC} hover:opacity-70 transition-opacity flex-shrink-0`}
          title="Peida banner"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default AlarmBanner;
